// Multi-select flag picker for feature editing.
//
// Shows selected flags as chips. Order matters: the FIRST chip is the
// PRIMARY flag — it drives the card badge, workspace hover, and the
// auto-derived announcement state on the feature. Non-primary chips can
// be promoted with the "↑" button. A search input below reveals a
// filtered list of unselected flags — click one to add it. No popover
// library needed; plain state + focus-managed list.
import { useEffect, useMemo, useRef, useState } from "react";
import { useFlags } from "@/api/queries";
import { flagRollout } from "@/lib/flagRollout";
import { cn } from "@/lib/cn";

interface FlagPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export function FlagPicker({ value, onChange }: FlagPickerProps) {
  const { data: flags, isPending } = useFlags();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = new Set(value);

  const matches = useMemo(() => {
    if (!flags) return [];
    const q = query.trim().toLowerCase();
    return flags
      .filter((f) => !selected.has(f.id))
      .filter((f) => {
        if (!q) return true;
        return (
          f.id.toLowerCase().includes(q) ||
          (f.description ?? "").toLowerCase().includes(q)
        );
      })
      .slice(0, 50); // cap the dropdown so it doesn't blow past the viewport
  }, [flags, query, selected]);

  const add = (id: string) => {
    onChange([...value, id]);
    setQuery("");
    setOpen(false); // collapse after picking one — reopen by focusing again
  };

  const remove = (id: string) => {
    onChange(value.filter((v) => v !== id));
  };

  // Move a flag to position 0 so it becomes the primary — drives the
  // badge / hover / auto-derived state downstream.
  const promoteToPrimary = (id: string) => {
    onChange([id, ...value.filter((v) => v !== id)]);
  };

  // Close the dropdown on any pointer press outside the picker. A plain
  // focus/blur close is unreliable (clicking empty space in a modal doesn't
  // always move focus), so listen for real outside clicks while open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div ref={containerRef} className="space-y-2">
      {/* Selected chips — flag id links to /flags/:id (new tab so mid-edit
          form state survives), and the × removes it from the selection.
          Bumped visual weight (heavier border, mobile-soft bg) so chips
          separate cleanly from the enclosing card. */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((id, idx) => {
            const flag = flags?.find((f) => f.id === id);
            const missing = flags && !flag;
            const isPrimary = idx === 0;
            return (
              <span
                key={id}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[11px]",
                  missing
                    ? "border-destructive/40 bg-[#fee2e2] text-destructive"
                    : isPrimary
                      ? "border-mobile bg-mobile-soft text-mobile ring-1 ring-mobile/30"
                      : "border-mobile/40 bg-mobile-soft/70 text-mobile",
                )}
                title={
                  missing
                    ? "Flag not found in squapi"
                    : isPrimary
                      ? `${flag?.description ?? id} · primary (drives the card badge)`
                      : (flag?.description ?? id)
                }
              >
                {isPrimary && (
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-mobile/80">
                    Primary ·
                  </span>
                )}
                <a
                  href={`/flags/${encodeURIComponent(id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline hover:underline"
                >
                  {id}
                </a>
                {!isPrimary && (
                  <button
                    type="button"
                    onClick={() => promoteToPrimary(id)}
                    aria-label={`Make ${id} the primary flag`}
                    title="Make primary"
                    className="text-mobile/60 hover:text-mobile"
                  >
                    ↑
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(id)}
                  aria-label={`Remove ${id}`}
                  className="text-mobile/60 hover:text-destructive"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={isPending ? "Loading flags…" : "Search flags to add…"}
          disabled={isPending}
          className="w-full rounded-md border border-border-hover bg-bg px-3 py-1.5 text-sm placeholder:text-text-tertiary focus:border-accent focus:outline-none"
        />

        {/* Dropdown */}
        {open && matches.length > 0 && (
          <ul
            role="listbox"
            className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-card shadow-lg"
          >
            {matches.map((f) => {
              const rollout = flagRollout(f);
              return (
                <li key={f.id}>
                  <button
                    type="button"
                    onClick={() => add(f.id)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-border/40"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="font-mono text-xs">{f.id}</span>
                      {f.description && (
                        <span className="ml-2 truncate text-xs text-text-tertiary">
                          {f.description}
                        </span>
                      )}
                    </span>
                    <span
                      className={cn(
                        "flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider",
                        rollout.kind === "ga"
                          ? "bg-ga-soft text-ga"
                          : rollout.kind === "ea"
                            ? "bg-ea-soft text-ea"
                            : rollout.kind === "unreleased"
                              ? "bg-nightly-soft text-nightly"
                              : "bg-border text-text-tertiary",
                      )}
                    >
                      {rollout.kind}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {open && query && matches.length === 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-text-tertiary shadow-lg">
            No flags match "{query}".
          </div>
        )}
      </div>
    </div>
  );
}
